import React from 'react';
import { Grid, Box, Typography, Alert, CircularProgress, Card } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import { t } from '../../../utils/textUtils';

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, subColor }) => (
  <Card sx={{ 
    p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', 
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, bgcolor: iconBg, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon sx={{ color: iconColor, fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.25, lineHeight: 1.2, wordBreak: 'break-word' }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: subColor || '#64748B', fontWeight: 500 }}>
          {sub}
        </Typography>
      </Box>
    </Box>
  </Card>
);

const FundSummarySection = ({ fundSummary, fundSummaryError, fundSummaryLoading, formatCurrency }) => {
  const pendingReleaseCount = Math.max((fundSummary.totalRequests || 0) - (fundSummary.releasedCount || 0), 0);
  const disbursementStatus = (() => {
    if ((fundSummary.totalRequests || 0) === 0) return { label: 'BELUM CAIR', color: '#64748B', bg: '#F1F5F9' };
    if ((fundSummary.releasedCount || 0) === (fundSummary.totalRequests || 0)) return { label: 'FULLY PAID', color: '#166534', bg: '#DCFCE7' };
    if (fundSummary.releasedCount > 0) return { label: 'PARTIAL', color: '#92400E', bg: '#FEF3C7' };
    return { label: 'APPROVED', color: '#1D4ED8', bg: '#DBEAFE' };
  })();

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 2,
          background: 'linear-gradient(135deg, #059669, #10B981)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 10px rgba(5,150,105,0.3)'
        }}>
          <AccountBalanceWalletIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
            {t('RINGKASAN DANA')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>Rekapitulasi pengajuan dana project</Typography>
        </Box>
      </Box>

      {fundSummaryError ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{fundSummaryError}</Alert>
      ) : fundSummaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <KpiCard
              icon={AccountBalanceWalletIcon}
              iconBg="#DCFCE7"
              iconColor="#16A34A"
              label={t('Total Uang Cair')}
              value={formatCurrency(fundSummary.totalAmount)}
              sub={`${fundSummary.totalRequests || 0} pengajuan disetujui`}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <KpiCard
              icon={RequestQuoteIcon}
              iconBg="#FEF3C7"
              iconColor="#D97706"
              label={t('Jumlah Pengajuan')}
              value={`${fundSummary.totalRequests || 0} Pengajuan`}
              sub={pendingReleaseCount > 0 ? `${pendingReleaseCount} menunggu pencairan` : 'Semua sudah dicairkan'}
              subColor={pendingReleaseCount > 0 ? '#D97706' : '#16A34A'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <KpiCard
              icon={CheckCircleIcon}
              iconBg={disbursementStatus.bg}
              iconColor={disbursementStatus.color}
              label={t('Status Pencairan')}
              value={disbursementStatus.label}
              sub={`${fundSummary.releasedCount || 0} / ${fundSummary.totalRequests || 0} dicairkan`}
              subColor={disbursementStatus.color}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <KpiCard
              icon={BarChartIcon}
              iconBg="#EEF2FF"
              iconColor="#6366F1"
              label={t('Rata-rata per Pengajuan')}
              value={formatCurrency(fundSummary.averagePerRequest)}
              sub={fundSummary.totalRequests > 0 ? `berdasarkan ${fundSummary.totalRequests} pengajuan` : 'Belum ada data'}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FundSummarySection;
